import type { AxiosInstance } from 'axios';

import type { PartListItem } from '@accio3d/shared-ui/printability/services/printability2dService';
import { fetchPaginatedList } from '@accio3d/shared-ui/services/pagination';
import type { PagePaginatedResponse, PaginationParams } from '@accio3d/shared-ui/types/pagination';

export interface PartOverview {
  partId: number;
  partName: string;
  partNumber: string;
  type: string;
  partProcessingStatus: string;
  owner: string;
  category: string;
  priority: string | null;
  naturalLanguageMessage: string;
  hasSpec: boolean;
  hasDrawing2d: boolean;
  hasCad3d: boolean;
  fileUrls?: string[];
  targetPrice: number | null;
  surfaceFinish?: string | null;
  tolerances?: string | null;
  estimatedRoi: { rangeFrom: number; rangeTo: number };
}

export const PART_SUMMARY_FILTERS = [
  'complete',
  'data_issues',
  'candidates',
  'matched_equip',
  'matched_supplier',
] as const;

export type PartSummaryFilter = (typeof PART_SUMMARY_FILTERS)[number];

export const PART_TIERS = ['1', '2', '3', '4', '5', 'processing', 'awaiting', 'unscored'] as const;

export type PartTier = (typeof PART_TIERS)[number];

export interface PartListFilters {
  search?: string;
  filter?: PartSummaryFilter | null;
  tier?: PartTier | null;
  status?: string;
}

export interface PartsBreakdown {
  total: number;
  tiers: Record<PartTier, number>;
  insights: {
    completeData: number;
    dataIssues: number;
    candidates: number;
    matchedEquipment: number;
    matchedSupplier: number;
  };
}

function toFilterParams(filters?: PartListFilters): Record<string, string> {
  const params: Record<string, string> = {};
  if (filters?.search) params.search = filters.search;
  if (filters?.filter) params.filter = filters.filter;
  if (filters?.tier) params.tier = filters.tier;
  if (filters?.status) params.status = filters.status;
  return params;
}

export function createPartsService(client: AxiosInstance) {
  return {
    async list(
      pagination?: PaginationParams,
      filters?: PartListFilters,
    ): Promise<PagePaginatedResponse<PartListItem>> {
      return fetchPaginatedList<PartListItem>(client, '/purchasers/parts', pagination, {
        params: toFilterParams(filters),
      });
    },

    async getBreakdown(): Promise<PartsBreakdown> {
      const { data } = await client.get<PartsBreakdown>('/purchasers/parts/breakdown');
      return data;
    },

    async getOverview(partId: string): Promise<PartOverview> {
      const { data } = await client.get<PartOverview>(`/purchasers/parts/${partId}/overview`);
      return data;
    },

    async deletePart(partId: string): Promise<void> {
      await client.delete(`/purchasers/parts/${partId}`);
    },
  };
}

export type PartsService = ReturnType<typeof createPartsService>;
